import type { AnchorHTMLAttributes, ReactNode } from 'react'

import { isNil } from 'ramda'

import { useDesignSystem } from '@twisto/base'

type Props = {
  /**
   * Internal path. When provided, BaseLink renders the host-provided
   * `LinkComponent` from DesignSystemContext (e.g. Next's `<Link>`) so
   * the host router handles navigation. Falls back to a plain `<a href>`
   * when the host hasn't supplied a LinkComponent.
   */
  to?: string
  children?: ReactNode
  className?: string
  replace?: boolean
} & AnchorHTMLAttributes<HTMLAnchorElement>

export const BaseLink = ({
  to,
  children,
  className,
  replace,
  ...other
}: Props) => {
  const { LinkComponent } = useDesignSystem()

  if (!isNil(to)) {
    const Component = LinkComponent ?? 'a'

    return (
      <Component
        data-testid="base-link"
        {...other}
        {...(other.target === '_blank' && { rel: 'noopener noreferrer' })}
        className={className}
        href={to}
        replace={replace}
      >
        {children}
      </Component>
    )
  }

  return (
    <a
      className={className}
      data-testid="base-link"
      {...other}
      {...(other.target === '_blank' && { rel: 'noopener noreferrer' })}
    >
      {children}
    </a>
  )
}
